import { IRowNode } from '@ag-grid-community/core';
import { Component, OnInit } from '@angular/core';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions, GridRow } from '@app/core/components/grid/grid.types';
import { DataTaskService } from '@app/core/services/data-task.service';
import { FileService } from '@app/core/services/file.service';
import { ModalService } from '@app/core/services/modal.service';
import { TranslateService } from '@app/core/services/translate.service';
import { LogisticService, StockplaceItem } from '@app/services/logistic.service';
import { MergeDocument, PdfService } from '@app/services/pdf.service';

@Component({
    selector: 'tt-concern-orders-grid',
    templateUrl: './concern-orders-grid.component.html',
    styleUrls: ['./concern-orders-grid.component.css'],
})
export class ConcernOrdersGridComponent extends ComponentBaseComponent implements OnInit {
    public get?: ConcernOrdersGridGet;

    public stockplaces: StockplaceItem[] = [];

    public createGrid?: GridOptions;

    public printedGrid?: GridOptions;

    public previousGrid?: GridOptions;

    private translations: { [key: string]: string } = {
        failed_to_fetch_file_with_filename: '',
        missing_the_following_files: '',
    };

    constructor(
        private datatask: DataTaskService,
        private file: FileService,
        private pdf: PdfService,
        private modal: ModalService,
        private translate: TranslateService,
        private logistic: LogisticService,
    ) {
        super();
    }

    private async getConsignmentLabels(params: { container_id: string; consignment_keyno: number }): Promise<string[]> {
        return (await this.datatask.Post(3687, params)).map((label: { docfilename: string }) => label.docfilename);
    }

    public readGrid() {
        if (this.get?.selected_tab === 'previous') {
            if (!this.previousGrid) {
                this.buildPreviousGrid();
            } else {
                this.previousGrid.gridfunc?.read();
            }
        } else if (this.get?.selected_tab === 'printed') {
            if (!this.printedGrid) {
                this.buildPrintedGrid();
            } else {
                this.printedGrid?.gridfunc?.read();
            }
        } else if (this.get?.selected_tab === 'create') {
            if (!this.createGrid) {
                this.buildCreateGrid();
            } else {
                this.createGrid?.gridfunc?.read();
            }
        }
    }

    private buildCreateGrid() {
        this.createGrid = new Grid()
            .setRememberId('w_concern_orders_grid.create_grid')
            .setLoadDataFunction({ method: 3663, parameters: () => ({ ...this.get, status_id: 0 }) })
            .setSaveDataFunction({ refreshRowOnSave: true })
            .setRemoveRowFunction({ confirm: false })
            .setSpecialFunc({
                buttons: [
                    { name: 'reprint_container_label', func: this.downloadContainerConsignmentLabels.bind(this), icon: 'far fa-file-pdf', tooltip: 'download_all_consignment_labels_for_this_container', translateTooltip: true },
                    // { name: 'send_consignment', func: this.markContainerAsSent.bind(this), icon: 'far fa-paper-plane', tooltip: 'mark_container_as_sent', translateTooltip: true },
                ],
            });
    }

    private buildPrintedGrid() {
        this.printedGrid = new Grid()
            .setRememberId('w_concern_orders_grid.printed_grid')
            .setLoadDataFunction({ method: 3663, parameters: () => ({ ...this.get, status_id: 1 }) })
            .setSpecialFunc({
                groupButtons: [
                    { name: 'print_container_labels', field: 'container_id', func: this.downloadGroupedContainerLabel.bind(this), icon: 'fas fa-folder', tooltip: 'download_all_consignment_labels_for_this_container', translateTooltip: true },
                    { name: 'send_consignment', field: 'container_id', func: this.markContainerAsSent.bind(this), icon: 'fas fa-paper-plane', tooltip: 'mark_container_as_sent', translateTooltip: true },
                ],
                buttons: [{ name: 'reprint_consignment_label', func: this.downloadConsignmentLabel.bind(this), icon: 'fas fa-file-pdf', tooltip: 'download_consignment_label_for_this_consignment', translateTooltip: true }],
            });
    }

    private buildPreviousGrid() {
        this.previousGrid = new Grid()
            .setRememberId('w_concern_orders_grid.previous_grid')
            .setLoadDataFunction({ method: 3664, parameters: () => this.get })
            .setSpecialFunc({
                groupButtons: [{ name: 'print_container_labels', field: 'container_id', func: this.downloadGroupedContainerLabel.bind(this), icon: 'fas fa-folder', tooltip: 'download_all_consignment_labels_for_this_container', translateTooltip: true }],
                buttons: [{ name: 'reprint_consignment_label', func: this.downloadConsignmentLabel.bind(this), icon: 'far fa-file-pdf', tooltip: 'download_consignment_label_for_this_consignment', translateTooltip: true }],
            });
    }

    private async downloadGroupedContainerLabel(row: IRowNode | GridRow) {
        if (!!row.key) {
            await this.getConsignmentLabelPdf({ container_id: row.key, consignment_keyno: 0, filename: `consignmentlabels_container_${row.key}.pdf` });
        }
    }

    private async downloadContainerConsignmentLabels(row: GridRow) {
        await this.getConsignmentLabelPdf({ container_id: row['container_id'], consignment_keyno: 0, filename: `consignmentlabels_container_${row['container_id']}.pdf` });
        this.createGrid?.gridfunc?.removeRow(row);
    }

    private downloadConsignmentLabel(row: GridRow) {
        this.getConsignmentLabelPdf({ container_id: '', consignment_keyno: row['consignment_keyno'], filename: `consignmentlabel_consignment_${row['consignment_keyno']}.pdf` });
    }

    private async markContainerAsSent(row: GridRow | IRowNode) {
        const doMarkAsSent = await this.modal.openConfirmDialog({ type: 'warning', title: 'od_confirm_mark_as_sent_title', translateTitle: true, message: 'od_confirm_mark_as_sent_message', translateMessage: true });

        if (doMarkAsSent) {
            const { errorcode, errormessage } = (await this.datatask.Post(3689, { container_id: row.key }))[0];

            if ('' + errorcode === '0') {
                this.previousGrid?.gridfunc?.read();
            } else {
                this.modal.show({ type: 'danger', title: 'error_occured', translateTitle: true, message: errormessage });
            }
        }
    }

    private async getConsignmentLabelPdf(params: { container_id: string; consignment_keyno: number; filename: string }) {
        const consignmentLabels = await this.getConsignmentLabels(params);
        const missingLabels: string[] = [];
        const progress = this.modal.openProgressDialog({ value: 0, max: consignmentLabels.length });

        progress.updateIndeterminate(true);

        let count = 0;

        const fileArrayBuffers = await Promise.all(
            consignmentLabels.map(async (label) => {
                try {
                    const arraybuffer = await this.file.download(this.base64UrlEncodeUnicode(label), { suppressErrorMessage: true });
                    progress.updateValue((count = count + 1));
                    let binary = '';
                    let bytes = new Uint8Array(arraybuffer);
                    let len = bytes.byteLength;
                    for (let i = 0; i < len; i++) {
                        binary += String.fromCharCode(bytes[i]);
                    }
                    console.log(window.btoa(binary));
                    return arraybuffer;
                } catch (error) {
                    missingLabels.push(label);
                    progress.updateStatus('invalid');
                    progress.updateInvalidText(this.translations['failed_to_fetch_file_with_filename'] + ': ' + label);
                    console.error(error);
                }

                return null;
            }),
        );

        if (missingLabels.length > 0) {
            this.modal.openErrorDialog(this.translations['missing_the_following_files'] + missingLabels.reduce((acc, label) => acc + '\n' + label, ''), { showCopyButton: true });
        }
        // @ts-ignore
        const mergeDocuments: MergeDocument[] = fileArrayBuffers.filter((arrayBuffer) => !!arrayBuffer).map((arrayBuffer) => ({ content: arrayBuffer, contentType: 'application/pdf' }));

        const pdf = await this.pdf.mergePDFs(mergeDocuments, { includeTableOfContents: false });
        this.downloadBase64Pdf(pdf, params.filename);
        progress.updateIndeterminate(false);
    }

    private downloadBase64Pdf(base64Data: string, filename: string) {
        const base64 = base64Data.replace(/^data:application\/pdf;base64,/, '');

        const byteCharacters = atob(base64);
        const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    }

    private async translateWords() {
        const data = await this.translate.translateBatch(Object.keys(this.translations));

        Object.keys(data).forEach((key) => (this.translations[key] = data[key] ?? ''));
    }

    private async loadStockplaces() {
        this.stockplaces = await this.logistic.getStockplaces();
    }

    private async loadGet() {
        this.get = (await this.datatask.Post(3662))[0];

        if (this.get?.selected_tab === 'previous') {
            if (!this.previousGrid) {
                this.buildPreviousGrid();
            }
        }

        if (this.get?.selected_tab === 'printed') {
            if (!this.printedGrid) {
                this.buildPrintedGrid();
            }
        }

        if (this.get?.selected_tab === 'create') {
            if (!this.createGrid) {
                this.buildCreateGrid();
            }
        }
    }

    ngOnInit(): void {
        this.loadStockplaces();
        this.loadGet();
        this.translateWords();
    }
}

interface ConcernOrdersGridGet {
    page_heading: string;
    date_fom: string;
    selected_tab: string;
    stockplace_id: string;
}
